// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx, ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {UserIconEl} from 'common/widgets/user-icon';
import {IconSizeType} from 'common/constants';
import {IronContextRegistry} from 'common/report/context-registry';
import {TopCreator} from 'common/report/queries/app-api/project-top-creators';
import {getCreatorNameLabel, getSuggestedCreators} from 'common/report/util/search';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {LocalizationStore} from 'common/i18n/store';

import css from './index.inline.styl';

type BrowseSuggestedCreatorsParams = {
  onCreatorClick?: (creator: TopCreator, position: number) => void;
  userId: number;
};

interface BrowseSuggestedCreatorsJsxProps {
  params: BrowseSuggestedCreatorsParams;
}

const CreatorCard = ({
  creator,
  onCreatorClick,
  position,
  userId,
  i18n,
}: {
  creator: TopCreator;
  onCreatorClick: (creator: TopCreator, position: number) => void;
  position: number;
  userId: number;
  i18n: LocalizationStore;
}) => {
  const {t} = i18n;
  const boardsLabel = t(`discover.suggested-creators.board`, {count: creator.dashboards_count});
  const reportsLabel = t(`discover.suggested-creators.report`, {count: creator.bookmarks_count});

  return (
    <ButtonEl
      sel='.browse-creator-button'
      attrs={{
        theme: ButtonTheme.Secondary,
        elref: `creatorCard`,
      }}
      on={{click: () => onCreatorClick(creator, position)}}
    >
      <div sel='.browse-creator-card'>
        <UserIconEl
          sel='.creator-card-icon'
          attrs={{
            id: creator.id,
            name: creator.name,
            email: creator.email,
            size: IconSizeType.Medium,
          }}
        />
        <div sel='.creator-text'>
          <div sel='.creator-card-name'>{getCreatorNameLabel(creator, userId)}</div>
          {/* `0 boards • 0 reports` case shouldn't happen, because we are not displaying users with 0 creations */}
          <div sel='.creator-entities-count'>{`${boardsLabel} • ${reportsLabel}`}</div>
        </div>
      </div>
    </ButtonEl>
  );
};

export class BrowseSuggestedCreators extends ParamComponent<
  BrowseSuggestedCreatorsParams,
  EmptyObj,
  IronContextRegistry
> {
  get config() {
    return {
      contexts: [`projectTopCreators` as const, `currentUser` as const, `i18n` as const],
      useShadowDom: true,
      css,
      template: () => {
        const creators = this.getSuggestedCreators();
        if (!creators.length) {
          return null;
        }

        return (
          <div sel='.browse-suggested-creators'>
            <div sel='.browse-suggested-creators-title'>{this.t(`discover.suggested-creators.title`)}</div>

            <div sel='.browse-suggested-creators-list'>
              {creators.map((creator, idx) => {
                return (
                  <CreatorCard
                    creator={creator}
                    onCreatorClick={this.params.onCreatorClick}
                    userId={this.params.userId}
                    position={idx}
                    i18n={this.getContext(`i18n`)}
                  />
                );
              })}
            </div>
          </div>
        );
      },
      params: {
        onCreatorClick: Function,
        userId: Number,
      },
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  getSuggestedCreators(): TopCreator[] {
    const topCreators = this.getContext(`projectTopCreators`).getCreators();
    return getSuggestedCreators(userCookieUtils.getUserIdentityFromCookie()?.id, topCreators);
  }
}

export const BrowseSuggestedCreatorsEl = defineAndMakeJsxFactory<BrowseSuggestedCreatorsJsxProps>(
  `browse-suggested-creators`,
  BrowseSuggestedCreators,
);
export const BrowseSuggestedCreatorsReact = panel2react<BrowseSuggestedCreatorsJsxProps>(
  `browse-suggested-creators`,
  BrowseSuggestedCreators,
);
